// Passwordless OTP auth via Cognito custom-auth flow (Amplify v6).
//
// Flow:
//   1. startSignIn(username)  -> Cognito CreateAuthChallenge texts/emails the
//      code (server-side). Returns { needsCode: true }.
//   2. confirmOtp(code)       -> VerifyAuthChallengeResponse checks it
//      server-side. On success Cognito issues tokens.
//   3. getIdToken() / getGroups() read the *verified* token. The OTP is never
//      compared in the browser, and the code is never returned by any API.
//
// This replaces the old flow (fetch the contact row, read `otp`, compare in
// JS). `username` is the participant's phone in E.164 or their email.
import {
  signIn,
  signUp,
  confirmSignIn,
  fetchAuthSession,
  signOut as amplifySignOut,
  getCurrentUser,
} from "aws-amplify/auth";
import "./amplifyConfig";

const needsCodeStep = (nextStep) =>
  nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE";

// Cognito requires a password on sign-up even in a passwordless flow. It is
// never used again (login is OTP-only), so generate a throwaway strong one.
const randomPassword = () => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return (
    "Aa1!" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  );
};

// Phone numbers are E.164 ("+1..."); anything else is treated as an email.
const isPhone = (username) => username.startsWith("+");

const startCustomAuth = (username) =>
  signIn({username, options: {authFlowType: "CUSTOM_WITHOUT_SRP"}});

/**
 * Begin passwordless sign-in; triggers the OTP send. If the user doesn't
 * exist yet, auto-create it (open self-signup) and retry — so a participant's
 * first sign-in with the phone/email from their link just works.
 */
export const startSignIn = async (username) => {
  // Clear any in-flight/previous session first. Amplify v6 rejects a second
  // signIn while a challenge is already in progress (e.g. the user taps
  // "resend"), and a stale session would block a fresh attempt. signOut is a
  // safe no-op when there's nothing to clear.
  try {
    await amplifySignOut();
  } catch {
    /* nothing to clear */
  }
  try {
    const {nextStep} = await startCustomAuth(username);
    return {needsCode: needsCodeStep(nextStep)};
  } catch (err) {
    if (err?.name !== "UserNotFoundException") throw err;
    // First-time user → create the account, then start auth.
    await signUp({
      username,
      password: randomPassword(),
      options: {
        userAttributes: isPhone(username)
          ? {phone_number: username}
          : {email: username},
      },
    });
    const {nextStep} = await startCustomAuth(username);
    return {needsCode: needsCodeStep(nextStep)};
  }
};

/** Submit the OTP the user received. Resolves to true on success. */
export const confirmOtp = async (code) => {
  const {isSignedIn} = await confirmSignIn({challengeResponse: code});
  return isSignedIn;
};

/** Verified ID token JWT, or null if not signed in. */
export const getIdToken = async () => {
  const session = await fetchAuthSession();
  return session?.tokens?.idToken?.toString() ?? null;
};

/** Groups from the verified token (e.g. ["admin"]); [] for learners. */
export const getGroups = async () => {
  const session = await fetchAuthSession();
  const raw = session?.tokens?.idToken?.payload?.["cognito:groups"];
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
};

export const isAdmin = async () => (await getGroups()).includes("admin");

export const getCurrentUserSafe = async () => {
  try {
    return await getCurrentUser();
  } catch {
    return null;
  }
};

export const signOut = () => amplifySignOut();
